import jsPDF from "jspdf";
import { currency, percent, RESET_CTA_URL } from "./calc";

const NAVY = [17, 47, 107];
const GOLD = [230, 184, 106];
const INK = [31, 41, 55];
const MUTED = [91, 101, 118];
const CREAM = [248, 244, 238];

export function generateReport(inputs, results, lead, logoDataUrl) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  let y = 0;

  // ---- Header band (navy) ----
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 100, "F");

  // Gold accent stripe
  doc.setFillColor(...GOLD);
  doc.rect(0, 100, pageW, 4, "F");

  // Logo on the left
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", margin, 22, 60, 60);
    } catch (e) { /* ignore */ }
  }

  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFont("times", "bold");
  doc.setFontSize(22);
  doc.text("Your Financial Fitness Report", margin + 75, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(230, 220, 200);
  doc.text("Finlightenment — Financial Enlightenment Awaits", margin + 75, 68);
  doc.text(new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  }), margin + 75, 84);

  y = 130;

  // ---- Lead intro ----
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(`Prepared for ${lead?.name || "you"}`, margin, y);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...MUTED);
  const intro =
    "This report is a personalized snapshot of your current spending and how a small shift toward your future can meaningfully change your retirement outcome. It is a general educational tool — not personalized advice.";
  const introLines = doc.splitTextToSize(intro, pageW - margin * 2);
  doc.text(introLines, margin, y);
  y += introLines.length * 13 + 12;

  // ---- Big highlight box: opportunity ----
  doc.setFillColor(...NAVY);
  doc.roundedRect(margin, y, pageW - margin * 2, 78, 10, 10, "F");
  doc.setTextColor(230, 220, 200);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(
    `YOUR ${results.extraSavingsPercent}% OPPORTUNITY`,
    margin + 16,
    y + 22
  );
  doc.setTextColor(...GOLD);
  doc.setFont("times", "bold");
  doc.setFontSize(28);
  doc.text(currency(results.extraImpactToday), margin + 16, y + 52);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Additional projected value (in today's dollars)", margin + 16, y + 68);
  y += 100;

  // ---- Section: Your inputs ----
  sectionTitle(doc, "Your Inputs", margin, y); y += 22;
  const inputRows = [
    ["Current age", `${inputs.age}`],
    ["Retirement age", `${inputs.retirementAge}`],
    ["Years to retirement", `${results.yearsToRetirement}`],
    ["Gross annual income", currency(inputs.grossIncome)],
    ["Annual take-home pay", currency(inputs.takeHomePay)],
    ["Take-home as % of gross", percent(results.estimatedNetRatio, 1)],
    ["Current retirement balance", currency(inputs.currentBalance)],
    ["Current savings rate", percent(inputs.retirementSavingRate, 1)],
  ];
  y = drawTwoColTable(doc, inputRows, margin, y, pageW - margin * 2);
  y += 10;

  // ---- Section: Where your money is going ----
  sectionTitle(doc, "Where Your Money Is Going (Take-home pay)", margin, y); y += 22;
  const budgetRows = [
    [`Needs (${Math.round(results.normalizedNeedsPct)}%)`, `${currency(results.needsAnnual)}/yr · ${currency(results.needsMonthly)}/mo`],
    [`Wants (${Math.round(results.normalizedWantsPct)}%)`, `${currency(results.wantsAnnual)}/yr · ${currency(results.wantsMonthly)}/mo`],
    [`Future (${Math.round(results.normalizedFuturePct)}%)`, `${currency(results.futureAnnual)}/yr · ${currency(results.futureMonthly)}/mo`],
  ];
  y = drawTwoColTable(doc, budgetRows, margin, y, pageW - margin * 2);
  y += 10;

  // ---- Section: Retirement projection ----
  sectionTitle(doc, "Retirement Projection (in today's dollars)", margin, y); y += 22;
  const projRows = [
    ["Age-based benchmark savings rate", percent(results.recommendedRate, 0)],
    ["Current path — projected value", currency(results.currentToday)],
    [`If +${results.extraSavingsPercent}% redirected — projected`, currency(results.increasedToday)],
    ["Difference", currency(results.extraImpactToday)],
  ];
  y = drawTwoColTable(doc, projRows, margin, y, pageW - margin * 2);
  y += 10;

  // Page break check
  if (y > pageH - 220) { doc.addPage(); y = margin; }

  // ---- Section: paycheck impact ----
  sectionTitle(doc, `Paycheck Impact of +${results.extraSavingsPercent}%`, margin, y); y += 22;
  const payRows = [
    ["Extra annual amount redirected", currency(results.extraAnnualContribution)],
    ["Roth — monthly take-home reduction", `${currency(results.rothTakeHomeReductionMonthly)}/mo`],
    ["Roth — annual take-home reduction", currency(results.rothTakeHomeReductionAnnual)],
    ["Pre-tax — monthly take-home reduction", `${currency(results.pretaxTakeHomeReductionMonthly)}/mo`],
    ["Pre-tax — annual take-home reduction", currency(results.pretaxTakeHomeReductionAnnual)],
    ["Daily impact (pre-tax)", `${currency(results.pretaxTakeHomeReductionDaily)}/day`],
    ["Daily impact (Roth)", `${currency(results.rothTakeHomeReductionDaily)}/day`],
  ];
  y = drawTwoColTable(doc, payRows, margin, y, pageW - margin * 2);
  y += 14;

  // ---- Takeaway box ----
  if (y > pageH - 220) { doc.addPage(); y = margin; }
  doc.setFillColor(...CREAM);
  const takeaway = `Simple takeaway: your Future allocation currently sends about ${currency(results.futureAnnual)} per year toward future goals. Redirecting an additional ${results.extraSavingsPercent}% adds ${currency(results.extraAnnualContribution)} per year. If that extra goes to a Roth account, take-home pay drops by the full amount. If it goes to a pre-tax account, the paycheck impact is often closer to ${currency(results.pretaxTakeHomeReductionMonthly)} per month, depending on your tax rate.`;
  const tLines = doc.splitTextToSize(takeaway, pageW - margin * 2 - 20);
  const tH = tLines.length * 13 + 24;
  doc.roundedRect(margin, y, pageW - margin * 2, tH, 10, 10, "F");
  // Gold left border
  doc.setFillColor(...GOLD);
  doc.rect(margin, y, 4, tH, "F");
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.text(tLines, margin + 14, y + 18);
  y += tH + 16;

  // ---- Next step / CTA ----
  if (y > pageH - 200) { doc.addPage(); y = margin; }
  doc.setFillColor(...NAVY);
  doc.roundedRect(margin, y, pageW - margin * 2, 130, 12, 12, "F");
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("FINANCIAL FOUNDATION RESET", margin + 18, y + 26);
  doc.setTextColor(255, 255, 255);
  doc.setFont("times", "bold");
  doc.setFontSize(16);
  const headline = "Build a budget that works — and finally make your money support the life you want.";
  const hLines = doc.splitTextToSize(headline, pageW - margin * 2 - 36);
  doc.text(hLines, margin + 18, y + 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(230, 220, 200);
  const reset = "A three-week guided experience that pairs a strong money mindset with a practical budgeting system.";
  doc.text(doc.splitTextToSize(reset, pageW - margin * 2 - 36), margin + 18, y + 50 + hLines.length * 18 + 8);

  // CTA button
  const btnY = y + 130 - 38;
  doc.setFillColor(...GOLD);
  doc.roundedRect(margin + 18, btnY, 280, 26, 13, 13, "F");
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.textWithLink("Click here → Financial Foundation Reset", margin + 28, btnY + 17, {
    url: RESET_CTA_URL,
  });

  y += 150;

  // ---- Footer / disclaimer ----
  if (y > pageH - 90) { doc.addPage(); y = margin; }
  doc.setTextColor(...MUTED);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8.5);
  const disc = "Educational use only. This tool is for general financial education — not personalized advice. Results are estimates based on simplified assumptions and do not include employer match, Social Security, pensions, or full tax details. Actual results will vary.";
  doc.text(doc.splitTextToSize(disc, pageW - margin * 2), margin, y);

  // Save
  const safeName = (lead?.name || "report").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`finlightenment-fitness-report-${safeName}.pdf`);
}

function sectionTitle(doc, title, x, y) {
  doc.setFont("times", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...NAVY);
  doc.text(title, x, y);
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.4);
  const w = doc.getTextWidth(title);
  doc.line(x, y + 4, x + w, y + 4);
}

function drawTwoColTable(doc, rows, x, y, w) {
  const rowH = 22;
  doc.setDrawColor(220, 220, 230);
  rows.forEach((row, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(248, 244, 238);
      doc.rect(x, y, w, rowH, "F");
    }
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(row[0], x + 12, y + 14);
    doc.setTextColor(...INK);
    doc.setFont("helvetica", "bold");
    const valW = doc.getTextWidth(row[1]);
    doc.text(row[1], x + w - 12 - valW, y + 14);
    y += rowH;
  });
  return y;
}
